import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isMovedFromLayout, matchingLayoutShape, readLayoutShapes } from "@/office/powerpoint/design";
import { isPlaceholder, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionZuruecksetzen: Lesson = {
  id: "zuruecksetzen",
  title: "Zurücksetzen: der unterschätzte Knopf",
  summary: "Wie verschobene Platzhalter mit einem Klick wieder an ihren Platz kommen.",
  durationMinutes: 9,
  learningGoals: [
    "Platzhalter an ihrer Layoutposition belassen",
    "„Zurücksetzen“ gezielt einsetzen",
    "Erkennen, was Zurücksetzen kann und was nicht",
  ],
  steps: [
    {
      id: "warum-nicht-verschieben",
      title: "Warum Platzhalter dort bleiben sollten, wo sie sind",
      explanation: [
        "Ein Platzhalter lässt sich anfassen und verschieben wie jedes andere Objekt. " +
          "Genau das macht ihn verwundbar: Ein unbeabsichtigter Zug mit der Maus, und der " +
          "Titel sitzt auf Folie 7 drei Millimeter tiefer als auf allen anderen.",
        "Beim Durchblättern sieht man das sofort – der Titel springt. Dieses Springen " +
          "ist der häufigste Grund dafür, dass eine Präsentation unruhig wirkt, ohne dass " +
          "man den Finger darauf legen kann. Es liegt nicht an den Farben oder der " +
          "Schrift, sondern daran, dass die Elemente nicht auf derselben Linie liegen.",
        "Wenn ein Element wirklich woanders hin soll, gehört diese Entscheidung nicht auf " +
          "die einzelne Folie, sondern ins Layout. Dort gilt sie für alle Folien, die " +
          "dieses Layout verwenden – und bleibt konsistent.",
      ],
      keyPoints: [
        "Ein verschobener Platzhalter fällt beim Durchblättern als Springen auf.",
        "Positionsänderungen gehören ins Layout, nicht auf die einzelne Folie.",
        "Ansicht → Folienmaster öffnet Master und Layouts zum Bearbeiten.",
      ],
    },
    {
      id: "zuruecksetzen-anwenden",
      title: "Zurücksetzen anwenden",
      explanation: [
        "„Start“ → „Zurücksetzen“ ist der Knopf, den kaum jemand kennt und fast jeder " +
          "brauchen könnte. Er stellt für alle Platzhalter der markierten Folie die " +
          "Position, Größe und Formatierung wieder her, die das Layout vorsieht. Gelöschte " +
          "Platzhalter kommen dabei zurück, verschobene rücken an ihren Platz.",
        "Wichtig ist, was er nicht anfasst: den Inhalt. Der Text bleibt vollständig " +
          "erhalten, nur seine Verpackung wird zurechtgerückt. Zurücksetzen ist damit " +
          "ungefährlich und lässt sich bedenkenlos auf eine ganze Präsentation anwenden – " +
          "alle Folien in der Miniaturleiste mit Strg + A markieren und einmal drücken.",
        "Der eine Fall, in dem Zurücksetzen nicht hilft: frei gezogene Textfelder. Die " +
          "gehören zu keinem Layout, also gibt es auch keinen Zustand, auf den " +
          "zurückgesetzt werden könnte. Sie bleiben genau da liegen, wo sie sind. Auch " +
          "das ist ein Argument für Platzhalter.",
      ],
      keyPoints: [
        "Start → Zurücksetzen richtet alle Platzhalter der Folie nach dem Layout aus.",
        "Der Inhalt bleibt dabei unverändert erhalten.",
        "Auf freie Textfelder wirkt Zurücksetzen nicht.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass kein Platzhalter deiner Folien von seiner Layoutposition " +
          "abweicht – am schnellsten mit „Start“ → „Zurücksetzen“.",
        hint: "Alle Folien in der Miniaturleiste mit Strg + A markieren, dann Start → Zurücksetzen.",
        check: async () => {
          const snapshot = await readPresentation();
          const layoutShapes = await readLayoutShapes();

          if (!snapshot.features.shapeDetails || layoutShapes === null) {
            return nichtPruefbar(
              "Die Sollposition der Platzhalter im Layout",
              "1.4",
              "Blättere die Folien durch und achte darauf, ob der Titel von Folie zu Folie springt.",
            );
          }

          const verschoben: Array<{ folienNummer: number; name: string }> = [];
          for (const folie of snapshot.slides) {
            for (const form of folie.shapes.filter(isPlaceholder)) {
              const vorlage = matchingLayoutShape(
                folie.layoutId,
                form.placeholder?.type ?? null,
                form.name,
                layoutShapes,
              );
              if (vorlage && isMovedFromLayout(form, vorlage)) {
                verschoben.push({
                  folienNummer: folie.index + 1,
                  name: form.name ?? "unbenannter Platzhalter",
                });
              }
            }
          }

          if (verschoben.length === 0) {
            return pass(
              "Alle Platzhalter sitzen an der Position, die ihr Layout vorgibt – nichts springt beim Durchblättern.",
            );
          }

          const erste = verschoben[0];
          return fail(
            `${verschoben.length === 1 ? "Ein Platzhalter weicht" : `${verschoben.length} Platzhalter weichen`} von der Layoutposition ab, ` +
              `zum Beispiel „${erste.name}“ auf Folie ${erste.folienNummer}.`,
            "Folie markieren und „Start“ → „Zurücksetzen“ drücken. Soll die Position dauerhaft anders sein, " +
              "ändere sie im Layout unter „Ansicht“ → „Folienmaster“ statt auf der einzelnen Folie.",
          );
        },
      },
    },
    {
      id: "grenzen",
      title: "Was Zurücksetzen nicht kann",
      explanation: [
        "Zurücksetzen arbeitet immer gegen das Layout, das der Folie gerade zugewiesen " +
          "ist. Wenn die Folie das falsche Layout trägt, setzt es sie sauber auf das " +
          "falsche Layout zurück. Der erste Blick gilt deshalb immer dem Layout, erst der " +
          "zweite dem Zurücksetzen.",
        "Ebenfalls unberührt bleiben Bilder, Formen, Diagramme und Tabellen, die nicht in " +
          "einem Platzhalter stecken, sowie alles, was auf dem Folienmaster selbst liegt.",
        "Ein Sonderfall ist Text, dessen Größe von Hand geändert wurde. Zurücksetzen " +
          "stellt die Schriftgröße des Layouts wieder her – was gewollt ist, aber " +
          "überraschen kann, wenn jemand eine lange Aufzählung absichtlich verkleinert " +
          "hat. Der bessere Umgang mit zu langem Text steht im nächsten Modul: kürzen, " +
          "nicht verkleinern.",
      ],
      keyPoints: [
        "Erst das Layout prüfen, dann zurücksetzen.",
        "Bilder, Formen und Diagramme außerhalb von Platzhaltern bleiben unberührt.",
        "Von Hand verkleinerte Schrift wird auf die Layoutgröße zurückgestellt.",
      ],
    },
  ],
};
